/**
 * Review Interest Dialog — lead owner's review of a DSA commission proposal.
 *
 * Used by:
 *   - dsa-offers/SentInvites               (lead owner ← DSA's response)
 *   - leadflow/tabs/ReceivedInterestsTab   (lead owner ← DSA's marketplace interest)
 *
 * Backend payload (verified against MarketplaceInterestController::formatInterestResponse):
 *   interest.{id, status, status_label, status_severity, status_icon}
 *   interest.{proposed_percentage, counter_percentage, final_percentage}
 *   interest.{interest_message, counter_message, rejection_reason}
 *   interest.{negotiation_round, is_owner_turn, expires_at}
 *   interest.interested_agent.{id, name, avatar}
 *   interest.lead.{id, borrower_name, product_type, loan_amount, city, state, quality_grade}
 *   interest.negotiations[] + available_actions[]  (detail endpoint only)
 *
 * Sizing density matches DsaOfferCard.js (canonical v2.0):
 *   text-xs body baseline, text-sm emphasis, text-base hero. Sub-text-xs icons
 *   via inline fontSize (0.65–0.85rem). No `<style>` blocks. No inline padding.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Inplace, InplaceDisplay, InplaceContent } from 'primereact/inplace';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import {
    marketplaceService,
    getInterestStatusConfig,
    formatLoanAmount,
    formatTimeAgo,
} from '../../../services/marketplaceService';

const MSG_PREVIEW_CHARS = 140;

// ── helpers ────────────────────────────────────────────────────────────────

// Indian rupee shorthand: ₹1.75 L, ₹2.5 Cr, etc.
// Rounds to whole rupees below ₹1L to avoid JS float-precision artefacts
// (e.g. 300000 * 0.07 / 100 = 210.00000000000003).
const formatRupees = (n) => {
    if (n == null || isNaN(Number(n))) return null;
    const v = Number(n);
    if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
    if (v >= 100000)   return `₹${(v / 100000).toFixed(2)} L`;
    return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

const trim3 = (n) => parseFloat(Number(n || 0).toFixed(3));

// Severity → PrimeFlex colour token (for bg-{color}-100 / text-{color}-600).
const sevColor = (s) => ({
    success: 'green', danger: 'red', warning: 'orange',
    info: 'blue',     help: 'purple',
}[s] || 'gray');

// action_type → severity + icon + display label.
const actionMeta = (item) => {
    const t = item?.action_type;
    if (t === 'accepted')           return { severity: 'success', icon: 'pi pi-check',  label: 'Accepted'  };
    if (t === 'rejected')           return { severity: 'danger',  icon: 'pi pi-times',  label: 'Declined'  };
    if (t === 'counter_offer')      return { severity: 'warning', icon: 'pi pi-sync',   label: 'Counter'   };
    if (t === 'interest_expressed') return { severity: 'info',    icon: 'pi pi-star',   label: 'Interest'  };
    if (t === 'new_proposal')       return { severity: 'help',    icon: 'pi pi-send',   label: 'Proposal'  };
    if (t === 'counter_accepted')   return { severity: 'success', icon: 'pi pi-check',  label: 'Counter accepted' };
    if (t === 'withdrawn')          return { severity: 'secondary', icon: 'pi pi-undo', label: 'Withdrawn' };
    return { severity: 'secondary', icon: 'pi pi-circle', label: (t || 'Update').replace(/_/g, ' ') };
};

// Lead quality_grade A-E → severity.
const gradeSeverity = (g) => {
    if (!g) return null;
    const c = String(g).toUpperCase().charAt(0);
    if (c === 'A') return 'success';
    if (c === 'B') return 'info';
    if (c === 'C') return 'warning';
    return 'danger';
};

// Expiry hint from ISO string.
const expiryHint = (isoStr) => {
    if (!isoStr) return null;
    const ms = new Date(isoStr).getTime() - Date.now();
    if (isNaN(ms) || ms <= 0) return { label: 'Expired', severity: 'danger' };
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor(ms / 3600000);
    if (days >= 2)  return { label: `Expires in ${days}d`, severity: 'secondary' };
    if (days === 1) return { label: 'Expires tomorrow',     severity: 'warning'   };
    if (hours >= 2) return { label: `Expires in ${hours}h`, severity: 'warning'   };
    return              { label: 'Expires soon',            severity: 'danger'    };
};

// ── component ──────────────────────────────────────────────────────────────

const ReviewInterestDialog = ({ visible, onHide, interest, onSuccess }) => {
    const toast = useRef(null);

    const [actionChoice,   setActionChoice]   = useState('accept');
    const [counterPct,     setCounterPct]     = useState(3.0);
    const [counterMessage, setCounterMessage] = useState('');
    const [rejectReason,   setRejectReason]   = useState('');
    const [submitting,     setSubmitting]     = useState(false);
    const [negotiations,   setNegotiations]   = useState([]);
    const [negotiationsLoading, setNegotiationsLoading] = useState(false);

    // ── derived ────────────────────────────────────────────────────────────
    const agent       = interest?.interested_agent || {};
    const agentName   = agent.name || 'Agent';
    const agentAvatar = agent.avatar;
    // Below fields not yet shipped server-side — render IFF data present so
    // adding them to formatInterestResponse later just lights up the UI.
    const agentRating  = agent.rating;
    const agentDeals   = agent.completed_deals;
    const agentReply   = agent.avg_response_hours;
    const agentVerified = !!agent.verified;
    const hasTrustChips = agentRating != null || agentDeals != null || agentReply != null;

    const lead         = interest?.lead || null;
    const leadAmount   = lead?.loan_amount ?? null;
    const proposedPct  = trim3(interest?.proposed_percentage);
    const earnings     = leadAmount != null ? formatRupees(leadAmount * proposedPct / 100) : null;
    const gradeSev     = gradeSeverity(lead?.quality_grade);

    const expiry = useMemo(() => expiryHint(interest?.expires_at), [interest?.expires_at]);

    const delta = trim3(counterPct - proposedPct);
    const deltaLabel = Math.abs(delta) < 0.001 ? 'Same as agent'
        : delta < 0 ? `${trim3(Math.abs(delta))}% lower`
        : `${delta}% higher`;
    const deltaClass = Math.abs(delta) < 0.001 ? 'text-500'
        : delta < 0 ? 'text-orange-500'
        : 'text-green-600';

    const statusConfig = getInterestStatusConfig(interest?.status);

    // available_actions comes from the detail endpoint; fall back to is_owner_turn.
    const availableActions = interest?.available_actions || [];
    const canRespond = availableActions.length > 0
        ? availableActions.some((a) => ['accept', 'reject', 'counter'].includes(a))
        : (interest?.is_owner_turn || interest?.status === 'pending');

    // Hide history accordion for trivial round-1 cases — nothing useful inside.
    const showHistory = (interest?.negotiation_round || 0) > 1 || negotiations.length > 1;

    // ── fetch negotiation history ──────────────────────────────────────────
    const fetchNegotiationHistory = useCallback(async () => {
        if (!interest?.id) return;
        setNegotiationsLoading(true);
        try {
            const response = await marketplaceService.getInterestDetails(interest.id);
            if (response?.success) setNegotiations(response.data?.negotiations || []);
        } catch (e) {
            console.error('Failed to fetch negotiation history:', e?.message || e);
        } finally {
            setNegotiationsLoading(false);
        }
    }, [interest?.id]);

    useEffect(() => {
        if (!visible || !interest) return;
        setActionChoice('accept');
        // Default counter to the agent's proposed % — the negotiated amount on
        // this collaboration. User adjusts from that starting point.
        setCounterPct(Number(interest.proposed_percentage) || 3.0);
        setCounterMessage('');
        setRejectReason('');
        fetchNegotiationHistory();
    }, [visible, interest, fetchNegotiationHistory]);

    // ── handlers ───────────────────────────────────────────────────────────
    const handleAccept = () => {
        confirmDialog({
            message: earnings
                ? `Accept this offer? You'll earn ${earnings} (${proposedPct}%) on disbursal.`
                : `Accept this enquiry at ${proposedPct}% lead commission?`,
            header: 'Confirm accept',
            icon: 'pi pi-check-circle',
            acceptClassName: 'p-button-success',
            accept: async () => {
                setSubmitting(true);
                try {
                    const response = await marketplaceService.acceptInterest(interest.id);
                    if (response?.success) {
                        toast.current?.show({ severity: 'success', summary: 'Accepted', detail: 'Collaboration is now active.' });
                        setTimeout(() => { onHide(); onSuccess?.(response.data); }, 300);
                    }
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.message || 'Failed to accept enquiry' });
                } finally { setSubmitting(false); }
            },
        });
    };

    const handleCounter = async () => {
        if (Math.abs(delta) < 0.001) {
            toast.current?.show({ severity: 'warn', summary: 'No change', detail: 'A counter must differ from the agent’s offer.' });
            return;
        }
        setSubmitting(true);
        try {
            const response = await marketplaceService.counterInterest(interest.id, {
                counterPercentage: counterPct,
                message: counterMessage.trim() || undefined,
            });
            if (response?.success) {
                toast.current?.show({ severity: 'success', summary: 'Counter sent', detail: 'Waiting for the agent’s response.' });
                onSuccess?.(response.data);
                onHide();
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.message || 'Failed to send counter' });
        } finally { setSubmitting(false); }
    };

    const handleReject = async () => {
        setSubmitting(true);
        try {
            const response = await marketplaceService.rejectInterest(interest.id, {
                reason: rejectReason.trim() || undefined,
            });
            if (response?.success) {
                toast.current?.show({ severity: 'info', summary: 'Declined', detail: 'The agent has been notified.' });
                onSuccess?.(response.data);
                onHide();
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.message || 'Failed to decline' });
        } finally { setSubmitting(false); }
    };

    if (!interest) return null;

    // ── header ─────────────────────────────────────────────────────────────
    const dialogHeader = (
        <div className="flex align-items-center gap-2 flex-wrap">
            <Tag
                value={statusConfig.label || 'Pending'}
                severity={statusConfig.severity || 'warning'}
                icon={statusConfig.icon ? `pi ${statusConfig.icon}` : 'pi pi-clock'}
                className="text-xs"
            />
            <span className="text-900 font-semibold text-sm">Review enquiry</span>
            {expiry && (
                <Tag value={expiry.label} severity={expiry.severity} icon="pi pi-clock" className="text-xs" />
            )}
            {interest.negotiation_round > 1 && (
                <Tag value={`Round ${interest.negotiation_round}`} severity="secondary" className="text-xs ml-auto" />
            )}
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={onHide}
                header={dialogHeader}
                modal
                draggable={false}
                closable={!submitting}
                closeOnEscape={!submitting}
                style={{ width: '90vw', maxWidth: '480px' }}
                pt={{
                    content: { className: 'p-3' },
                    header:  { className: 'px-3 py-2' },
                }}
            >
                <div className="flex flex-column gap-3">

                    {/* ── 1. Agent identity (WHO) ───────────────────────── */}
                    <div className="surface-card border-round border-1 surface-border p-3">
                        <div className="flex align-items-center gap-2">
                            <Avatar
                                image={agentAvatar}
                                label={agentName.charAt(0).toUpperCase()}
                                shape="circle"
                                className="bg-primary text-white flex-shrink-0"
                                style={{ width: '2.25rem', height: '2.25rem', fontSize: '0.9rem' }}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex align-items-center gap-1 flex-wrap">
                                    <span className="font-semibold text-900 text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                        {agentName}
                                    </span>
                                    {agentVerified && (
                                        <i className="pi pi-verified text-primary" style={{ fontSize: '0.75rem' }} title="Verified" aria-label="Verified" />
                                    )}
                                </div>
                            </div>
                        </div>
                        {hasTrustChips && (
                            <div className="flex align-items-center gap-1 flex-wrap mt-2">
                                {agentRating != null && <Chip label={`★ ${agentRating}`} className="text-xs" />}
                                {agentDeals  != null && <Chip label={`${agentDeals} deals`} className="text-xs" />}
                                {agentReply  != null && <Chip label={`Replies ~${agentReply}h`} className="text-xs" />}
                            </div>
                        )}
                    </div>

                    {/* ── 2. What they're offering (WHAT) — green band ── */}
                    <div className="border-round border-1 surface-border p-3 text-center" style={{ backgroundColor: 'var(--green-50)' }}>
                        <div className="text-green-700 text-xs font-medium">
                            {earnings ? "You'll earn" : 'Offering you'}
                        </div>
                        <div className="text-green-700 font-bold text-base mt-1">
                            {earnings || `${proposedPct}%`}
                        </div>
                        {earnings && (
                            <div className="text-600 text-xs mt-1">
                                {proposedPct}% on {formatLoanAmount(leadAmount)} disbursal
                            </div>
                        )}
                        {interest.counter_percentage != null && (
                            <div className="text-xs mt-1">
                                <span className="text-500">Your last counter:</span>{' '}
                                <span className="text-orange-600 font-semibold">{trim3(interest.counter_percentage)}%</span>
                            </div>
                        )}
                    </div>

                    {/* ── 3. Lead context — borrower + product + location ── */}
                    {lead && (
                        <div className="surface-50 border-round border-1 surface-border p-3">
                            <div className="flex align-items-start gap-2">
                                <i className="pi pi-user text-primary flex-shrink-0 mt-1" style={{ fontSize: '0.8rem' }} aria-hidden="true" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex align-items-center gap-2">
                                        <span className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis flex-1 min-w-0">
                                            {lead.borrower_name || 'Lead'}
                                        </span>
                                        {lead.quality_grade && gradeSev && (
                                            <Tag
                                                value={`Grade ${String(lead.quality_grade).toUpperCase()}`}
                                                severity={gradeSev}
                                                className="text-xs flex-shrink-0"
                                            />
                                        )}
                                    </div>
                                    <div className="flex align-items-center gap-1 flex-wrap text-xs text-600 mt-1">
                                        {lead.product_type && (
                                            <span className="flex align-items-center gap-1">
                                                <i className="pi pi-tag" style={{ fontSize: '0.65rem' }} aria-hidden="true" />
                                                {lead.product_type}
                                            </span>
                                        )}
                                        {lead.loan_amount != null && (
                                            <>
                                                <span className="text-400">·</span>
                                                <span className="font-semibold text-700">{formatLoanAmount(lead.loan_amount)}</span>
                                            </>
                                        )}
                                        {(lead.city || lead.state) && (
                                            <>
                                                <span className="text-400">·</span>
                                                <span className="flex align-items-center gap-1">
                                                    <i className="pi pi-map-marker" style={{ fontSize: '0.65rem' }} aria-hidden="true" />
                                                    {[lead.city, lead.state].filter(Boolean).join(', ')}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── 4. Their message ─────────────────────────────── */}
                    {interest.interest_message && (
                        <div className="surface-card border-round border-1 surface-border border-left-3 border-primary p-3">
                            {interest.interest_message.length <= MSG_PREVIEW_CHARS ? (
                                <div className="flex align-items-start gap-2">
                                    <i className="pi pi-comment text-primary mt-1" style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                                    <div className="text-700 text-xs line-height-3 flex-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        &ldquo;{interest.interest_message}&rdquo;
                                    </div>
                                </div>
                            ) : (
                                <Inplace closable closeIcon="pi pi-chevron-up">
                                    <InplaceDisplay>
                                        <div className="flex align-items-start gap-2">
                                            <i className="pi pi-comment text-primary mt-1" style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                                            <div className="text-700 text-xs line-height-3 flex-1">
                                                &ldquo;{interest.interest_message.slice(0, MSG_PREVIEW_CHARS).trim()}&hellip;&rdquo;{' '}
                                                <span className="text-primary font-medium">Read more</span>
                                            </div>
                                        </div>
                                    </InplaceDisplay>
                                    <InplaceContent>
                                        <div className="flex align-items-start gap-2">
                                            <i className="pi pi-comment text-primary mt-1" style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                                            <div className="text-700 text-xs line-height-3 flex-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                &ldquo;{interest.interest_message}&rdquo;
                                            </div>
                                        </div>
                                    </InplaceContent>
                                </Inplace>
                            )}
                        </div>
                    )}

                    {/* ── 5+6. Action segments + panel, or status ─────── */}
                    {canRespond ? (
                        <>
                            <div className="flex gap-2">
                                <Button
                                    label="Accept"
                                    icon="pi pi-check"
                                    severity="success"
                                    outlined={actionChoice !== 'accept'}
                                    size="small"
                                    className="flex-1"
                                    onClick={() => setActionChoice('accept')}
                                />
                                <Button
                                    label="Counter"
                                    icon="pi pi-sync"
                                    severity="warning"
                                    outlined={actionChoice !== 'counter'}
                                    size="small"
                                    className="flex-1"
                                    onClick={() => setActionChoice('counter')}
                                />
                                <Button
                                    label="Decline"
                                    icon="pi pi-times"
                                    severity="danger"
                                    outlined={actionChoice !== 'decline'}
                                    size="small"
                                    className="flex-1"
                                    onClick={() => setActionChoice('decline')}
                                />
                            </div>

                            {actionChoice === 'accept' && (
                                <div className="surface-50 border-round border-1 surface-border p-3 flex flex-column gap-2 align-items-center text-center">
                                    <div className="text-700 text-xs">
                                        Accept {agentName}&rsquo;s offer.
                                        {earnings && (
                                            <> They&rsquo;ll pay you <strong className="text-green-700">{earnings}</strong> on disbursal.</>
                                        )}
                                    </div>
                                    <Button
                                        label="Confirm and accept"
                                        icon="pi pi-check"
                                        severity="success"
                                        size="small"
                                        className="w-full"
                                        onClick={handleAccept}
                                        loading={submitting}
                                    />
                                </div>
                            )}

                            {actionChoice === 'counter' && (
                                <div className="surface-50 border-round border-1 surface-border p-3 flex flex-column gap-3">
                                    <div className="text-center">
                                        <div className="text-500 text-xs font-medium uppercase mb-2">
                                            Your counter offer
                                        </div>
                                        <InputNumber
                                            value={counterPct}
                                            onValueChange={(e) => setCounterPct(Math.min(10, Math.max(0.5, e.value ?? 0.5)))}
                                            mode="decimal"
                                            minFractionDigits={1}
                                            maxFractionDigits={3}
                                            min={0.5}
                                            max={10}
                                            suffix="%"
                                            inputClassName="text-center font-bold text-orange-600 text-base w-7rem"
                                        />
                                    </div>

                                    {/* Live rupee preview + delta vs agent's offer */}
                                    {leadAmount != null && (
                                        <div className="text-center">
                                            <div className="text-xs text-700">
                                                You&rsquo;d earn{' '}
                                                <span className="font-bold text-orange-600">
                                                    {formatRupees(leadAmount * counterPct / 100)}
                                                </span>
                                            </div>
                                            {Math.abs(delta) >= 0.001 ? (
                                                <div className={`text-xs font-medium mt-1 ${deltaClass}`}>
                                                    {delta > 0 ? '+' : '−'}{formatRupees(Math.abs(leadAmount * delta / 100))} {delta > 0 ? 'more than' : 'less than'} agent&rsquo;s offer
                                                </div>
                                            ) : (
                                                <div className="text-xs text-500 mt-1">Same as agent&rsquo;s offer — adjust to send</div>
                                            )}
                                        </div>
                                    )}

                                    <InputTextarea
                                        value={counterMessage}
                                        onChange={(e) => setCounterMessage(e.target.value)}
                                        rows={2}
                                        autoResize
                                        className="w-full text-xs"
                                        placeholder="Add a note to your counter (optional)"
                                        maxLength={500}
                                    />

                                    <Button
                                        label="Send counter offer"
                                        icon="pi pi-send"
                                        severity="warning"
                                        className="w-full"
                                        onClick={handleCounter}
                                        loading={submitting}
                                        disabled={Math.abs(delta) < 0.001}
                                    />
                                </div>
                            )}

                            {actionChoice === 'decline' && (
                                <div className="surface-50 border-round border-1 surface-border p-3 flex flex-column gap-2">
                                    <InputTextarea
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        rows={3}
                                        autoResize
                                        className="w-full text-xs"
                                        placeholder="Reason (optional — helps the agent improve)"
                                        maxLength={500}
                                    />
                                    <Button
                                        label="Decline enquiry"
                                        icon="pi pi-times"
                                        severity="danger"
                                        outlined
                                        size="small"
                                        className="w-full"
                                        onClick={handleReject}
                                        loading={submitting}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="surface-card border-round border-1 surface-border p-2 flex align-items-center gap-2">
                            <i
                                className={`pi ${statusConfig.icon || 'pi-info-circle'} text-${sevColor(statusConfig.severity)}-500`}
                                style={{ fontSize: '1rem' }}
                                aria-hidden="true"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="text-900 font-semibold text-sm">{statusConfig.label}</div>
                                <div className="text-500 text-xs">
                                    {interest.status === 'accepted'  && 'Collaboration is active.'}
                                    {interest.status === 'rejected'  && 'This enquiry was declined.'}
                                    {interest.status === 'countered' && 'Waiting for the agent’s response.'}
                                    {interest.status === 'withdrawn' && 'The agent withdrew this enquiry.'}
                                    {interest.status === 'expired'   && 'This enquiry has expired.'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── 7. Past rounds — only if rounds > 1 ──────────── */}
                    {showHistory && (
                        <Accordion>
                            <AccordionTab
                                header={(
                                    <span className="flex align-items-center gap-2 text-xs font-medium">
                                        <i className="pi pi-history text-primary" style={{ fontSize: '0.75rem' }} aria-hidden="true" />
                                        Past rounds
                                        {negotiationsLoading
                                            ? <i className="pi pi-spin pi-spinner text-primary" style={{ fontSize: '0.7rem' }} aria-hidden="true" />
                                            : <Tag value={negotiations.length} severity="info" className="text-xs" />
                                        }
                                    </span>
                                )}
                            >
                                {negotiationsLoading ? (
                                    <div className="flex flex-column gap-2">
                                        {[1, 2].map((i) => (
                                            <div key={i} className="flex justify-content-end">
                                                <Skeleton width="70%" height="3rem" className="border-round" />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    /* WhatsApp-style chat: LO view → owner = You (right) */
                                    <div className="flex flex-column gap-2">
                                        {negotiations.map((item, idx) => {
                                            const meta = actionMeta(item);
                                            const isYou = item.actor_role === 'owner';
                                            const actorLabel = isYou ? 'You' : (item.actor_name || 'Agent');
                                            const itemPct = item.proposed_percentage != null ? trim3(item.proposed_percentage) : null;
                                            return (
                                                <div key={idx} className={`flex ${isYou ? 'justify-content-end' : 'justify-content-start'}`}>
                                                    <div
                                                        className={`border-round p-2 ${isYou ? 'bg-primary-50' : 'surface-100'}`}
                                                        style={{ maxWidth: '85%' }}
                                                    >
                                                        <div className="flex align-items-center gap-1 flex-wrap mb-1">
                                                            <span className="text-900 font-medium text-xs">{actorLabel}</span>
                                                            <Tag value={meta.label} severity={meta.severity} className="text-xs" />
                                                            {itemPct != null && (
                                                                <span className="text-700 text-xs font-semibold">{itemPct}%</span>
                                                            )}
                                                        </div>
                                                        {item.message && (
                                                            <div className="text-700 text-xs mb-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                &ldquo;{item.message}&rdquo;
                                                            </div>
                                                        )}
                                                        <div className="text-400 text-xs text-right white-space-nowrap">
                                                            {item.time_ago || formatTimeAgo(item.created_at)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </AccordionTab>
                        </Accordion>
                    )}
                </div>
            </Dialog>
            <ConfirmDialog />
        </>
    );
};

export default ReviewInterestDialog;
